import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useTranslation } from 'react-i18next';
import { orderService } from '../../services/orderService';
import { PickingChannel } from './pickingChannels';
import { useChannelLabel } from './useChannelLabel';

interface PickingHomeProps {
  personnelName: string;
  warehouseId: string;
  onSelectChannel: (channel: PickingChannel) => void;
}

interface ChannelCardProps {
  label: string;
  count: number;
  color: string;
  logo: string;
  onClick: () => void;
}

const ChannelCard: React.FC<ChannelCardProps> = ({ label, count, color, logo, onClick }) => (
  <ButtonBase
    onClick={onClick}
    focusRipple
    sx={{
      width: '100%',
      textAlign: 'left',
      borderRadius: 4,
      p: { xs: 2.5, sm: 3 },
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 2, sm: 3 },
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        borderColor: color,
        boxShadow: `0 8px 24px ${color}26`,
        transform: 'translateY(-2px)',
      },
      '&:active': { transform: 'translateY(0)' },
    }}
  >
    <Box
      sx={{
        width: { xs: 72, sm: 92 },
        height: { xs: 60, sm: 72 },
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        p: 1,
      }}
    >
      {logo ? (
        <Box
          component="img"
          src={logo}
          alt={label}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <StorefrontIcon sx={{ color, fontSize: { xs: 40, sm: 52 } }} />
      )}
    </Box>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1.4rem', sm: '1.7rem' },
          lineHeight: 1.2,
          letterSpacing: '-0.01em',
        }}
      >
        {label}
      </Typography>
    </Box>

    <Typography
      sx={{
        color,
        fontWeight: 800,
        fontSize: { xs: '2rem', sm: '2.4rem' },
        lineHeight: 1,
        flexShrink: 0,
        minWidth: 48,
        textAlign: 'right',
      }}
    >
      {count}
    </Typography>

    <ChevronRightIcon sx={{ color, fontSize: { xs: 28, sm: 32 }, flexShrink: 0 }} />
  </ButtonBase>
);

const PickingHome: React.FC<PickingHomeProps> = ({ personnelName, warehouseId, onSelectChannel }) => {
  const { t } = useTranslation('warehouse');
  const { channels } = useChannelLabel(warehouseId);
  const [counts, setCounts] = useState<Record<string, number>>({});

  const loadCounts = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const res = await orderService.getChannelSummary(warehouseId);
      const data = res.data?.data ?? res.data ?? {};
      const channels: any[] = data.channels ?? data.Channels ?? [];
      const map: Record<string, number> = {};
      for (const c of channels) {
        const src = String(c.channel ?? c.Channel ?? '').toLowerCase();
        map[src] = c.totalOrders ?? c.TotalOrders ?? 0;
      }
      setCounts(map);
    } catch { /* ignore */ }
  }, [warehouseId]);

  useEffect(() => {
    loadCounts();
    const interval = setInterval(loadCounts, 5000);
    return () => clearInterval(interval);
  }, [loadCounts]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2, sm: 4 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.9rem', sm: '2.4rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            {t('screens.picking.home.greeting')}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.9rem', sm: '2.4rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: 'primary.main',
            }}
          >
            {personnelName}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 1.5, fontSize: { xs: '1rem', sm: '1.15rem' } }}
          >
            {t('screens.picking.home.channelPrompt')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
          {channels.length === 0 ? (
            <Typography color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {t('screens.picking.home.noChannels')}
            </Typography>
          ) : (
            channels.map((ch) => (
              <ChannelCard
                key={ch.source}
                label={ch.label}
                count={counts[ch.source] ?? 0}
                color={ch.color}
                logo={ch.logo}
                onClick={() => onSelectChannel(ch)}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PickingHome;
